import { excludeUndefinedDataKeys, IS_CLIENT, IS_PRODUCTION, sha256Hash } from '@repo/common-utils';

import { SendEventParams, SetUserParams, TiktokPixelInitializeProps } from '../types';

export class TiktokPixelService {
  private readonly pixelId: string;
  private preservedEvents: SendEventParams[] = [];
  private retryCount: number = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private userData: {
    id: string;
    email: string;
    phone: string;
  } | null = null;

  constructor({ tiktokPixelId }: TiktokPixelInitializeProps) {
    this.pixelId = tiktokPixelId ?? '';
    this.init();
  }

  private init() {
    if (!IS_CLIENT) return;
    this.sendInitEvent();
  }

  sendEvent(params: SendEventParams) {
    if (typeof window.ttq !== 'object' || !window.ttq?.track) {
      this.preservedEvents.push(params);
      return;
    }

    const eventNameParams = [params?.category, params?.object, params?.detail, params?.action];
    const event = eventNameParams.filter((value) => !!value).join('_');

    const isExistAmountAttribute = typeof params?.attributes?.amount !== 'undefined';
    const eventParams = excludeUndefinedDataKeys({
      ...params?.attributes,
      value: isExistAmountAttribute ? Number(params.attributes?.amount ?? 0) : undefined,
      currency: isExistAmountAttribute ? 'KRW' : undefined
    });

    if (params?.action === 'viewed') {
      window.ttq.page();
      this.printLog('PageView', eventParams);
      return;
    }
    window.ttq.track(event, eventParams);
    this.printLog(event, eventParams);
  }

  async setUser({ id = '', email = '', phone = '' }: SetUserParams) {
    if (typeof window.ttq !== 'object' || !window.ttq?.identify) {
      this.userData = { id, email, phone };
      return;
    }
    window.ttq.identify({ external_id: id, email: await sha256Hash(email), phone_number: await sha256Hash(phone) });
    this.userData = null;
    this.printLog('SetUser', { id, email, phone });
  }

  unsetUser() {
    if (typeof window.ttq !== 'object' || !window.ttq?.identify) return;
    window.ttq.identify({ external_id: '', email: '', phone_number: '' });
    this.printLog('UnsetUser', {});
  }

  private sendInitEvent() {
    if (typeof window.ttq === 'object' && window.ttq?.load) {
      window.ttq.load(this.pixelId);
      this.printLog('Init', { pixelId: this.pixelId });
      if (this.userData) this.setUser(this.userData);
      this.preservedEvents.forEach((event) => this.sendEvent(event));
      this.preservedEvents = [];
      return;
    } else if (this.retryCount < 10) {
      if (this.retryTimeout) clearTimeout(this.retryTimeout);
      this.retryTimeout = setTimeout(() => {
        this.retryCount++;
        this.sendInitEvent();
      }, 500);
    }
  }

  private printLog(event: string, params: Record<string, unknown>) {
    if (!IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true') {
      console.info('%c[TiktokPixel]', 'font-weight: bold;', event, [params]);
    }
  }
}
